import type { Job } from "@stryker/core/types/jobs";
import { booleanToYesNo } from "@/utils/mappings";
import { createInitialState as createMechanicalInitialState } from "@/utils/mappings/mechanical";
import { formatMaterialLabel } from "@/utils/materials";
import { formatAdditionalNotesFromIds } from "@/utils/notes";

type AdditionalNotesLookup = {
  notes: Array<{ id: number; name: string }>;
  notesById: Record<number, string>;
};

export const getMechanicalJobInfoBlocks = ({
  job,
  additionalNotesData,
  componentMaterialLabelById,
  enclosureMaterialLabelById,
}: {
  job: Job;
  additionalNotesData?: AdditionalNotesLookup;
  componentMaterialLabelById?: Record<number, string>;
  enclosureMaterialLabelById?: Record<number, string>;
}) => {
  const mechanicalSections = createMechanicalInitialState(job);
  const hasMeaningfulValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    return true;
  };
  const toTitleCase = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const formatComponentDetailsContent = () => {
    const content: Record<string, string> = {
      "Component Material": formatMaterialLabel(job?.component_material, componentMaterialLabelById),
      "Component Options":
        job?.component_options && job.component_options.length > 0 ? job.component_options.join(", ") : "N/A",
    };
    const components = job?.components_data ?? [];
    const componentEntries = components
      .map((component, index) => {
        const details: string[] = [];
        if (hasMeaningfulValue(component?.component_nps)) {
          details.push(`NPS: ${component.component_nps}`);
        }
        if (hasMeaningfulValue(component?.component_diameter)) {
          const unit = component?.unit_for_component_diameter ? ` ${component.unit_for_component_diameter}` : "";
          details.push(`Diameter: ${component.component_diameter}${unit}`);
        }
        if (hasMeaningfulValue(component?.component_schedule)) {
          details.push(`Schedule: ${component.component_schedule}`);
        }
        if (hasMeaningfulValue(component?.flange_rating)) {
          details.push(`Flange Rating: ${component.flange_rating}`);
        }
        if (hasMeaningfulValue(component?.component_thickness)) {
          const unit = component?.unit_for_component_thickness ? ` ${component.unit_for_component_thickness}` : "";
          details.push(`Thickness: ${component.component_thickness}${unit}`);
        }
        if (details.length === 0) {
          return null;
        }
        return { [`Component ${index + 1}`]: details.join("; ") };
      })
      .filter(Boolean) as Record<string, string>[];

    if (componentEntries.length === 0) {
      content["Components Data"] = "N/A";
    } else {
      Object.assign(content, ...componentEntries);
    }

    return content;
  };

  const formatDefectDetailsContent = () => {
    const content: Record<string, string> = {};
    const repairLengthOptions = job?.repair_length_options ?? [];
    if (repairLengthOptions.length > 0) {
      content["Repair Length Options"] = repairLengthOptions.join(", ");
    } else {
      content["Repair Length Options"] = "N/A";
    }
    const repairKitSizes = job?.repair_length_repair_kit_sizes ?? [];
    if (repairKitSizes.length > 0) {
      content["Repair Kit Sizes"] = repairKitSizes.join(", ");
    }

    const defects = job?.defects ?? [];
    const defectLabelPairs = [
      { valueKey: "total_defect_length", unitKey: "unit_for_total_defect_length", label: "Total Defect Length" },
      {
        valueKey: "desired_overall_length",
        unitKey: "unit_for_desired_overall_length",
        label: "Desired Overall Length",
      },
      {
        valueKey: "desired_inside_length",
        unitKey: "unit_for_desired_overall_length",
        label: "Desired Inside Length",
      },
      { valueKey: "defect_length_1", unitKey: "unit_for_defect_length_1", label: "Defect Length 1" },
      {
        valueKey: "desired_overall_length_header",
        unitKey: "unit_for_desired_overall_length_header",
        label: "Desired Overall Length Header",
      },
      {
        valueKey: "distance_from_center_of_riser_left",
        unitKey: "unit_for_distance_from_center_of_riser_left",
        label: "Distance from Center of Riser — Left",
      },
      {
        valueKey: "distance_from_center_of_riser_right",
        unitKey: "unit_for_distance_from_center_of_riser_right",
        label: "Distance from Center of Riser — Right",
      },
      { valueKey: "defect_length_2", unitKey: "unit_for_defect_length_2", label: "Defect Length 2" },
      { valueKey: "passed_header", unitKey: "unit_for_passed_header", label: "Passed Header" },
      { valueKey: "passed_weld_1", unitKey: "unit_for_passed_weld_1", label: "Passed Weld 1" },
      { valueKey: "passed_weld_2", unitKey: "unit_for_passed_weld_2", label: "Passed Weld 2" },
      { valueKey: "thickness_gap", unitKey: "unit_for_thickness_gap", label: "Thickness Gap" },
    ];

    defects.forEach((defect, index) => {
      const details: string[] = [];
      const usedKeys = new Set<string>();
      defectLabelPairs.forEach(({ valueKey, unitKey, label }) => {
        const value = (defect as any)?.[valueKey];
        if (hasMeaningfulValue(value)) {
          const unitValue = (defect as any)?.[unitKey];
          const unit = hasMeaningfulValue(unitValue) ? ` ${unitValue}` : "";
          details.push(`${label}: ${value}${unit}`);
          usedKeys.add(valueKey);
          usedKeys.add(unitKey);
        }
      });

      Object.entries(defect ?? {}).forEach(([key, value]) => {
        if (usedKeys.has(key) || key.startsWith("unit_for_")) {
          return;
        }
        if (hasMeaningfulValue(value)) {
          details.push(`${toTitleCase(key)}: ${String(value)}`);
        }
      });

      if (details.length > 0) {
        content[`Defect ${index + 1}`] = details.join("; ");
      }
    });

    if (defects.length === 0) {
      content.Defects = "N/A";
    }

    return content;
  };

  const formatStrongbackTypeLocation = (data: Record<string, unknown>) => {
    const content: Record<string, string> = {};
    const matchToEnclosure = data.match_strongback_to_enclosure;
    if (matchToEnclosure !== undefined && typeof matchToEnclosure === "boolean") {
      content["Match Strongback To Enclosure"] = booleanToYesNo(matchToEnclosure);
    }

    if ("strongback_header" in data) {
      content["Strongback Header"] = booleanToYesNo((data as any).strongback_header);
    }

    if ("strongback_riser" in data) {
      content["Strongback Riser"] = booleanToYesNo((data as any).strongback_riser);
    }

    if ("strongback_top" in data) {
      content["Strongback Top"] = booleanToYesNo((data as any).strongback_top);
    }

    if ("strongback_bottom" in data) {
      content["Strongback Bottom"] = booleanToYesNo((data as any).strongback_bottom);
    }

    const strongbacks = Array.isArray((data as any).strongbacks) ? ((data as any).strongbacks as any[]) : [];
    strongbacks.forEach((entry, index) => {
      const details: string[] = [];
      const distance = entry?.distance;
      const unitForDistance = entry?.unit_for_distance;

      if (hasMeaningfulValue(entry?.type)) {
        details.push(`Type: ${entry.type}`);
      }
      if (hasMeaningfulValue(distance)) {
        details.push(`Distance: ${distance}${unitForDistance ? ` ${unitForDistance}` : ""}`);
      }
      if (hasMeaningfulValue(entry?.flange_nps)) {
        details.push(`Flange NPS: ${entry.flange_nps}`);
      }
      if (hasMeaningfulValue(entry?.flange_rating)) {
        details.push(`Flange Rating: ${entry.flange_rating}`);
      }
      if (hasMeaningfulValue(entry?.custom_diameter)) {
        details.push(`Custom Diameter: ${entry.custom_diameter}`);
      }
      if (hasMeaningfulValue(entry?.raised_face_od)) {
        details.push(`Raised Face OD: ${entry.raised_face_od}`);
      }
      if (hasMeaningfulValue(entry?.flange_num_bolt_holes)) {
        details.push(`Flange Num Bolt Holes: ${entry.flange_num_bolt_holes}`);
      }
      if (hasMeaningfulValue(entry?.flange_stud_size)) {
        details.push(`Flange Stud Size: ${entry.flange_stud_size}`);
      }
      if (hasMeaningfulValue(entry?.flange_bolt_circle)) {
        details.push(`Flange Bolt Circle: ${entry.flange_bolt_circle}`);
      }

      if (details.length > 0) {
        content[`Strongback ${index + 1}`] = details.join("; ");
      }
    });

    return content;
  };

  const jobInfo = Object.entries(mechanicalSections)
    .map(([key, section]: [string, any]) => {
      if (key === "notes") {
        return null;
      }
      const newBlock: any = { title: "", content: {} };

      // Map section keys to display titles
      const titleMap: Record<string, string> = {
        jobDetails: "Job Details",
        additionalJobDetails: "Additional Job Details",
        contactInformation: "Contact Information",
        designConditions: "Design Conditions",
        repairType: "Repair Type",
        enclosureDetails: "Enclosure Details",
        strongbacks: "Strongback Material Details",
        strongbackTypeLocation: "Strongback Type and Location Detail",
        postConstructionTestingRequirements: "Post Construction Testing Requirements",
        sealingDetails: "Sealing Details",
        drawingNotes: "Drawing Notes",
        checks: "Checks",
        componentDetails: "Component Details",
        defectDetails: "Defect Details",
        notes: "Notes",
        finalDesign: "Final Design",
      };

      newBlock.title = titleMap[key] || key;

      if (key === "componentDetails") {
        newBlock.content = formatComponentDetailsContent();
        return newBlock;
      }

      if (key === "defectDetails") {
        newBlock.content = formatDefectDetailsContent();
        return newBlock;
      }

      // Convert section data to display format
      if (section?.data) {
        if (key === "strongbackTypeLocation") {
          newBlock.content = formatStrongbackTypeLocation(section.data);
          return newBlock;
        }

        const enclosureMaterialKeys = new Set([
          "shells",
          "endplates",
          "ears",
          "stud_bolts",
          "riser_shells",
          "flange_clamps",
          "strongbacks",
          "strongbacks_stud_bolts",
        ]);

        newBlock.content = Object.entries(section.data).reduce((acc: any, [fieldKey, fieldValue]) => {
          // Format field names for display
          const displayKey = toTitleCase(fieldKey);

          // Handle different data types
          if (Array.isArray(fieldValue)) {
            if (fieldKey === "additional_notes") {
              acc[displayKey] = formatAdditionalNotesFromIds(
                fieldValue as Array<string | number>,
                additionalNotesData?.notesById
              );
              return acc;
            }

            acc[displayKey] = fieldValue.length > 0 ? fieldValue.join(", ") : "N/A";
          } else if (typeof fieldValue === "boolean") {
            acc[displayKey] = booleanToYesNo(fieldValue);
          } else if (enclosureMaterialKeys.has(fieldKey)) {
            acc[displayKey] = formatMaterialLabel(fieldValue as string | number, enclosureMaterialLabelById);
          } else {
            acc[displayKey] = fieldValue || "N/A";
          }

          return acc;
        }, {});
      }

      return newBlock;
    })
    .filter((block) => block && Object.keys(block.content).length > 0);

  return jobInfo.map((block) => {
    if (block.title !== "Job Details") {
      return block;
    }

    const { "Job Number": jobNumber, ...remainingContent } = block.content;

    return {
      ...block,
      content: {
        "Job Name": job?.job_name ?? "N/A",
        ...(jobNumber ? { "Job Number": jobNumber } : {}),
        ...remainingContent,
      },
    };
  });
};
